import { Form } from "@inertiajs/react"
import { PartyPopper } from "lucide-react"
import { useTranslation } from "react-i18next"

import ProductThumbnail from "@/components/brand/settings/products/product-thumbnail"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { brandSettingsProductsFirstSyncAcknowledgementPath } from "@/routes"
import type { BrandSettingsProductsProps } from "@/types/products"

/**
 * OKS-960 — The first-import payoff (UX §2 S4).
 *
 * Rendered from a COMPLETED fact, never from a loading state: the server only
 * sends `first_sync_celebration` once a first successful import with at least
 * one product has landed and has neither been acknowledged nor superseded by a
 * later successful import (AC12/AC13). So it appears even if nobody was on the
 * page when the import finished — the state is durable, not a live event.
 *
 * `role="status"` and it does NOT steal focus: it arrives after a background
 * event, and yanking focus for a celebration would interrupt whatever the brand
 * was actually doing.
 *
 * ⚠️ If the first import TRUNCATED, this renders alongside `SyncPanel`'s
 * truncation notice. That is not a contradiction: the import succeeded and was
 * incomplete, and folding the two is the single most likely regression in this
 * rework (PRD D7).
 *
 * ## Both actions are gated, and NEITHER gates on `can_manage`
 *
 * - the CTA gates on `can_create_mission` — the policy that guards the
 *   destination (`MissionPolicy#new?`);
 * - the dismiss gates on nothing beyond seeing the page.
 *
 * ⚠️ `can_manage` is not a usable proxy in either direction:
 * `ProductPolicy#manage?` requires `tenant_admin?` while `MissionPolicy#new?`
 * does not, so a brand/agency member who cannot manage the catalogue CAN create
 * a mission — and a bare OKS admin can read this page but cannot.
 *
 * Dismissing is authorised by a READ predicate server-side, deliberately (PO
 * ruling): a viewer looking at a panel they cannot close is worse than Crew's
 * "DOM-absent, never disabled" rule, and acknowledging a notice is not a
 * business mutation.
 */
export default function FirstSyncCard({
  celebration,
  canCreateMission,
}: {
  celebration: NonNullable<BrandSettingsProductsProps["first_sync_celebration"]>
  canCreateMission: boolean
}) {
  const { t } = useTranslation()

  return (
    <Card data-testid="brand-products-first-sync" role="status">
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <PartyPopper className="size-5" aria-hidden />
          <h3 className="text-base font-semibold">
            {t("settings.brand.products.first_sync.title")}
          </h3>
        </div>

        <p className="text-muted-foreground text-sm">
          {t("settings.brand.products.first_sync.body", {
            domain: celebration.shop_domain,
          })}
        </p>

        {/* ⚠️ Composed EXACTLY as SyncPanel composes them, reusing the same two
            keys. Re-wording the counts here would give the brand two different
            sentences for one number on the same page. */}
        <p className="text-muted-foreground text-sm">
          {celebration.product_count !== null ? (
            <span data-testid="brand-products-first-sync-count">
              {t("settings.brand.products.sync.product_count", {
                count: celebration.product_count,
              })}
            </span>
          ) : null}
          {celebration.variant_count !== null ? (
            <>
              {" · "}
              <span>
                {t("settings.brand.products.sync.variant_count", {
                  count: celebration.variant_count,
                })}
              </span>
            </>
          ) : null}
        </p>

        {celebration.image_urls.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {celebration.image_urls.map((url) => (
              <ProductThumbnail key={url} src={url} alt="" />
            ))}
          </div>
        ) : null}

        {/* Both actions POST to the same endpoint: following the CTA also
            acknowledges (UX §2 S4), and the SERVER owns the destination via a
            closed `then` enum — a client-supplied URL here would be an open
            redirect on an authenticated POST. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Form
            action={brandSettingsProductsFirstSyncAcknowledgementPath()}
            method="post"
            options={{ preserveScroll: true }}
          >
            {({ processing }) => (
              <Button
                type="submit"
                variant="ghost"
                disabled={processing}
                className="h-11 w-full sm:h-9 sm:w-auto"
                data-testid="brand-products-first-sync-dismiss"
              >
                {t("settings.brand.products.first_sync.dismiss")}
              </Button>
            )}
          </Form>

          {canCreateMission ? (
            <Form
              action={brandSettingsProductsFirstSyncAcknowledgementPath()}
              method="post"
            >
              {({ processing }) => (
                <>
                  <input type="hidden" name="then" value="mission" />
                  <Button
                    type="submit"
                    disabled={processing}
                    className="h-11 w-full sm:h-9 sm:w-auto"
                    data-testid="brand-products-first-sync-cta"
                  >
                    {t("settings.brand.products.first_sync.cta")}
                  </Button>
                </>
              )}
            </Form>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
